import React from 'react';
import { makePrioStyles } from '../../theme/utils';

const useStyles = makePrioStyles((theme) => ({
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  skeleton: {
    backgroundColor: theme.colors.application.background.hover,
    borderRadius: theme.borderRadius.small,
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  skeletonCard: {
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
  },
  skeletonRow: {
    display: 'flex',
    gap: theme.spacing.regular,
    alignItems: 'center',
    marginBottom: theme.spacing.small,
  },
  skeletonText: {
    height: '14px',
    borderRadius: theme.borderRadius.small,
  },
  skeletonTitle: {
    height: '20px',
    borderRadius: theme.borderRadius.small,
  },
  skeletonCircle: {
    borderRadius: '50%',
  },
  skeletonButton: {
    height: '32px',
    borderRadius: theme.borderRadius.regular,
  },
  summaryCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing.regular,
    marginBottom: theme.spacing.regular,
  },
  summaryCardSkeleton: {
    '& > div:first-child': {
      marginBottom: theme.spacing.small,
    },
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing.regular,
    alignItems: 'end',
  },
  filterGroup: {
    '& > div:first-child': {
      marginBottom: theme.spacing.small,
    },
  },
  tableContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tableContent: {
    flex: 1,
    minHeight: '400px',
  },
  tableHeaders: {
    display: 'grid',
    gridTemplateColumns: '35% 15% 10% 10% 15% 15%',
    gap: theme.spacing.small,
    marginBottom: theme.spacing.regular,
    padding: theme.spacing.small,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '35% 15% 10% 10% 15% 15%',
    gap: theme.spacing.small,
    marginBottom: theme.spacing.small,
    padding: theme.spacing.small,
    borderRadius: theme.borderRadius.small,
  },
  tableRowEven: {
    backgroundColor: 'transparent',
  },
  tableRowOdd: {
    backgroundColor: `${theme.colors.application.background.hover}30`,
  },
  taskCell: {
    '& > div:first-child': {
      marginBottom: '4px',
    },
  },
  pillSkeleton: {
    borderRadius: '12px',
  },
  chartCard: {
    height: '320px',
  },
  scrollableChartCard: {
    height: '400px',
  },
  chartContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.regular,
  },
  chartList: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing.small,
  },
  chartItem: {
    marginBottom: theme.spacing.regular,
    padding: theme.spacing.small,
    borderRadius: theme.borderRadius.small,
  },
  chartItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.small,
  },
  dashboardContainer: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.regular,
  },
  bottomRow: {
    display: 'flex',
    gap: theme.spacing.regular,
    width: '100%',
    overflow: 'hidden',
  },
  bottomColumn: {
    flex: 1,
  },
  headerContainer: {
    display: 'flex',
    gap: theme.spacing.regular,
    alignItems: 'center',
    marginBottom: theme.spacing.regular,
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
  },
  moduleContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.colors.application.background.light,
  },
  navigationBar: {
    padding: `${theme.spacing.regular}px ${theme.spacing.large}px`,
    backgroundColor: theme.colors.application.background.default,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  moduleContent: {
    flex: 1,
    padding: theme.spacing.regular,
  },
  // New styles for updated layout
  chartsRow: {
    display: 'flex',
    gap: theme.spacing.regular,
    width: '100%',
    overflow: 'hidden',
    marginBottom: theme.spacing.regular,
  },
  fullWidthTable: {
    width: '100%',
    overflow: 'hidden',
  },
}));

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  className?: string;
}

// Base Skeleton Component
const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '14px',
  className = '',
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.skeleton} ${className}`}
      style={{
        width,
        height,
      }}
    />
  );
};

// Skeleton for Summary Cards
export const SummaryCardsSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.summaryCardsGrid}>
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          className={`${classes.skeletonCard} ${classes.summaryCardSkeleton}`}
        >
          <Skeleton width="60px" height="32px" />
          <Skeleton width="80%" height="14px" />
        </div>
      ))}
    </div>
  );
};

// Skeleton for Filters
export const FiltersSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.skeletonCard}>
      <div className={classes.filtersGrid}>
        {[...Array(7)].map((_, index) => (
          <div key={index} className={classes.filterGroup}>
            <Skeleton width="80px" height="14px" />
            <Skeleton width="100%" height="32px" />
          </div>
        ))}
        <Skeleton width="120px" height="32px" />
      </div>
    </div>
  );
};

// Skeleton for Table
export const TableSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={`${classes.skeletonCard} ${classes.tableContainer}`}>
      {/* Table Header */}
      <Skeleton width="200px" height="20px" className={classes.skeletonTitle} />

      {/* Table Content */}
      <div className={classes.tableContent}>
        {/* Table Headers */}
        <div className={classes.tableHeaders}>
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} width="80%" height="14px" />
          ))}
        </div>

        {/* Table Rows */}
        {[...Array(8)].map((_, rowIndex) => (
          <div
            key={rowIndex}
            className={`${classes.tableRow} ${
              rowIndex % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd
            }`}
          >
            {/* Task column */}
            <div className={classes.taskCell}>
              <Skeleton width="90%" height="14px" />
              <Skeleton width="60%" height="12px" />
            </div>

            {/* Project column */}
            <Skeleton width="85%" height="12px" />

            {/* Status column */}
            <Skeleton
              width="60px"
              height="24px"
              className={classes.pillSkeleton}
            />

            {/* Priority column */}
            <Skeleton
              width="50px"
              height="24px"
              className={classes.pillSkeleton}
            />

            {/* Assignee column */}
            <Skeleton
              width="70px"
              height="24px"
              className={classes.pillSkeleton}
            />

            {/* Due date column */}
            <Skeleton width="50px" height="12px" />
          </div>
        ))}
      </div>
    </div>
  );
};

interface BarChartSkeletonProps {
  title?: string;
}

// Skeleton for Bar Charts
export const BarChartSkeleton: React.FC<BarChartSkeletonProps> = ({
  title = 'Chart Title',
}) => {
  const classes = useStyles();

  return (
    <div className={`${classes.skeletonCard} ${classes.scrollableChartCard}`}>
      <Skeleton width="180px" height="20px" className={classes.skeletonTitle} />
      <div className={classes.chartList}>
        {[...Array(8)].map((_, index) => (
          <div key={index} className={classes.chartItem}>
            <div className={classes.chartItemHeader}>
              <Skeleton width={`${60 + Math.random() * 40}%`} height="14px" />
              <Skeleton width="40px" height="12px" />
            </div>
            <Skeleton width="100%" height="20px" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Dashboard Skeleton - Updated for new layout
export const TaskHubDashboardSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.dashboardContainer}>
      {/* Filters Skeleton */}
      <FiltersSkeleton />

      {/* Charts Row */}
      <div className={classes.chartsRow}>
        <div className={classes.bottomColumn}>
          <BarChartSkeleton title="Tasks by Assignee" />
        </div>
        <div className={classes.bottomColumn}>
          <BarChartSkeleton title="Tasks by Project" />
        </div>
      </div>

      {/* Full Width Table */}
      <div className={classes.fullWidthTable}>
        <TableSkeleton />
      </div>

      {/* Summary Cards Skeleton */}
      <SummaryCardsSkeleton />
    </div>
  );
};

// Header/Navigation Skeleton
export const TaskHubHeaderSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.navigationBar}>
      <div className={classes.headerContainer}>
        <Skeleton width="300px" height="32px" />
        <Skeleton width="400px" height="32px" />
      </div>
      <div className={classes.headerInfo}>
        <Skeleton width="120px" height="32px" />
        <Skeleton width="100px" height="12px" />
      </div>
    </div>
  );
};

// Complete Module Skeleton
export const TaskHubModuleSkeleton: React.FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.moduleContainer}>
      {/* Navigation Bar Skeleton */}
      <TaskHubHeaderSkeleton />

      {/* Content */}
      <div className={classes.moduleContent}>
        <TaskHubDashboardSkeleton />
      </div>
    </div>
  );
};

export default TaskHubDashboardSkeleton;
